import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  type CustomerHubTab,
  isCustomerHubTab,
} from '@/features/customers/components/customer-detail'
import { CustomerDetailView } from '@/features/customers/customers'
import { loadAccountingNamespaces } from '@/lib/i18n-namespaces'
import { hasGrant } from '@/lib/me'

export const Route = createFileRoute('/_authenticated/customers/$customerId/')({
  validateSearch: validateCustomerHubSearch,
  beforeLoad: async ({ context }) => {
    if (!hasGrant(context.queryClient, 'customer.view')) {
      throw redirect({ to: '/customers' })
    }
    // The statement tab labels its rows with the accounting document types, and
    // this route sits outside the accounting boundary that loads them.
    if (hasGrant(context.queryClient, 'accounting_report.view')) {
      await loadAccountingNamespaces()
    }
    return {
      canViewInvoices: hasGrant(context.queryClient, 'accounting_invoice.view'),
      canViewPayments: hasGrant(context.queryClient, 'accounting_payment.view'),
      canViewStatement: hasGrant(context.queryClient, 'accounting_report.view'),
    }
  },
  component: CustomerDetailRoute,
})

function CustomerDetailRoute() {
  const { tab } = Route.useSearch()
  const { canViewInvoices, canViewPayments, canViewStatement } = Route.useRouteContext()
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <CustomerDetailView
      tab={tab ?? 'overview'}
      canViewInvoices={canViewInvoices}
      canViewPayments={canViewPayments}
      canViewStatement={canViewStatement}
      onTabChange={(next) => void navigate({ search: (previous) => ({ ...previous, tab: next }) })}
    />
  )
}

export function validateCustomerHubSearch(search: Record<string, unknown>): {
  tab?: CustomerHubTab
} {
  return isCustomerHubTab(search.tab) ? { tab: search.tab } : {}
}
